import { useCallback, useRef } from 'react';

/**
 * Give focus back to the control that opened a modal surface.
 *
 * WHY THIS EXISTS. `@radix-ui/react-dialog`'s `DialogContentModal` installs its
 * own `onCloseAutoFocus`, which calls `event.preventDefault()` and then focuses
 * `context.triggerRef.current`. That `preventDefault()` kills FocusScope's own
 * sane fallback (`focus(previouslyFocusedElement ?? document.body)`), so for a
 * dialog rendered WITHOUT a `DialogTrigger` — every dialog whose `open` is
 * driven by state, which is most of ours — `triggerRef.current` is `null`,
 * nothing is focused at all, and the caret falls to `<body>`. The next Tab then
 * restarts from the top of the document. `AlertDialog` and our `Sheet` are
 * built on the same Content, so all three fail identically.
 *
 * HOW THE HANDOVER WORKS. Radix composes handlers with `composeEventHandlers`,
 * which runs OURS first and skips Radix's once the event is `defaultPrevented`.
 * So calling `preventDefault()` here STANDS DOWN the null-trigger focus rather
 * than racing it.
 *
 * "STANDING ASIDE" IS NOT A WORKING FALLBACK for a Dialog/AlertDialog/Sheet: if
 * we do nothing, Radix's handler runs next and preventDefaults FocusScope's
 * `previouslyFocusedElement ?? document.body` anyway, so a miss still lands on
 * `<body>`. It is only ever the least-bad answer for an opener that is gone.
 * THE CONTRACT IS THAT `capture()` RUNS ON EVERY PATH THAT OPENS THE SURFACE.
 *
 * TIMING. Radix dispatches `onCloseAutoFocus` from inside a `setTimeout(..., 0)`
 * in FocusScope's cleanup — after the panel has unmounted and after
 * `aria-hidden`'s undo has run — so by the time this handler fires the opener
 * is back in the accessibility tree and focusable again.
 */

/**
 * The opener, or a way to find it when the surface closes. A resolver is for
 * the case where the element does not exist yet at capture time, or is replaced
 * while the surface is open.
 */
export type ModalOpener = HTMLElement | null | (() => HTMLElement | null);

export type ModalReturnFocus = {
  /**
   * Call from the handler that OPENS the surface. Pass the element explicitly
   * wherever it is known — a menu item that is about to unmount with its menu
   * is not an opener worth remembering, and an opener blurred on purpose (so
   * Radix may legally `aria-hidden` the page behind the panel) cannot be read
   * back off the document at all. Omitted, it reads `document.activeElement`.
   */
  capture: (opener?: ModalOpener) => void;
  /** Hand to the Content's `onCloseAutoFocus`. */
  onCloseAutoFocus: (event: Event) => void;
  /**
   * The same hand-back, run by the CALLER, for a surface with no
   * `onCloseAutoFocus` seam to hang it on — a panel whose props are owned
   * elsewhere, say. Run it from a `setTimeout(..., 0)` beside the state change
   * that closes the surface, so it lands once the panel has actually UNMOUNTED.
   *
   * NOT because it has to queue behind Radix's own restore — it does not, and
   * measurably runs BEFORE it. The timer is there because the panel is still up
   * when the caller asks it to close: React has not committed the close yet, so
   * a synchronous `restore()` moves focus while FocusScope is still trapping,
   * and the trap pulls the caret straight back inside the panel. The panel then
   * unmounts and leaves it on `<body>` — the very failure this module exists to
   * prevent, reported as a hit.
   *
   * Radix's own close-focus handler runs after this timer and moves focus
   * nowhere for a trigger-less surface: it preventDefaults FocusScope's
   * `previouslyFocusedElement ?? document.body` fallback and focuses a null
   * `triggerRef`. So there is nothing here to be behind, and nothing to fight.
   *
   * Answers whether it focused anything, so a caller with a fallback of its own
   * knows when to use it. Spends the capture either way, exactly as closing
   * through the seam does.
   */
  restore: () => boolean;
};

/** `<body>` is the failure state this hook exists to prevent, never an opener. */
function rememberable(element: Element | null): HTMLElement | null {
  if (!element || element === document.body) return null;
  return element instanceof HTMLElement ? element : null;
}

/** A detached or disabled element takes focus silently nowhere, so it is not one. */
function focusable(element: HTMLElement | null): element is HTMLElement {
  return Boolean(element && element.isConnected && !element.matches('[disabled]'));
}

/**
 * An ORDER of openers, for a surface whose opener may not survive it.
 *
 * One remembered element answers only for surfaces that leave the page as they
 * found it. A confirmation that DELETES removes the row its kebab belonged to; a
 * bulk assignment empties the selection and takes the bar its button sat on. By
 * the time those close there is nothing to hand focus back to, and standing
 * aside means `<body>` — the failure this module exists to prevent.
 *
 * So a caller names the opener AND what should catch the caret if the opener is
 * gone, nearest first. Every candidate is resolved when the surface CLOSES —
 * which is also what lets a candidate name something that does not exist yet,
 * such as the card a renamed category is about to be re-rendered into — and each
 * is judged by the same `focusable` rule `restore()` applies, so "still usable"
 * cannot come to mean two different things on the two paths.
 */
export function firstFocusableOpener(...candidates: ModalOpener[]): () => HTMLElement | null {
  return () => {
    for (const candidate of candidates) {
      // `rememberable` first, because the usual first candidate is "whatever had
      // focus when the surface opened" and that is `<body>` whenever the control
      // was a menu item Radix had already torn down — the failure state, not a
      // candidate. `focusable` alone would happily hand `<body>` back.
      const element = rememberable(typeof candidate === 'function' ? candidate() : candidate);
      if (focusable(element)) return element;
    }
    return null;
  };
}

export function useModalReturnFocus(): ModalReturnFocus {
  const openerRef = useRef<ModalOpener>(null);

  const capture = useCallback((opener?: ModalOpener) => {
    // `undefined` means "read the document"; an explicit `null` means "there is
    // no opener", and must not fall back to whatever happens to have focus.
    openerRef.current = opener === undefined ? rememberable(document.activeElement) : opener;
  }, []);

  const restore = useCallback((): boolean => {
    const remembered = openerRef.current;
    // Spent on use: a second close with nothing captured must not re-steal focus
    // from wherever the person has moved on to.
    openerRef.current = null;

    const opener = typeof remembered === 'function' ? remembered() : remembered;
    if (!focusable(opener)) return false;

    opener.focus();
    return true;
  }, []);

  const onCloseAutoFocus = useCallback(
    (event: Event) => {
      // Only a HIT stands Radix's handler down. Radix composes the two and reads
      // `defaultPrevented` once ours has returned, so the order of the two
      // statements below is free — what matters is that a miss leaves the event
      // alone and lets the rest of the chain have its go, poor as it is (see
      // "STANDING ASIDE" above).
      if (restore()) event.preventDefault();
    },
    [restore],
  );

  return { capture, onCloseAutoFocus, restore };
}
